import { formatNumber } from 'util/formatNumber';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useUploadApi } from 'services/api';
import { useFileSelector } from './useFileSelector';

export interface ImageUploadProps {
  maxMb: number;
}

const BYTES_PER_MB = 2 ** 20;

export const useImageUpload = ({
  maxMb,
}: ImageUploadProps): {
  open: () => void;
  clear: () => void;
  progress: number | null;
  url: string | null;
} => {
  const [url, setUrl] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (progress !== 100) return;
    const timer = setTimeout(() => setProgress(null), 700);
    return () => clearTimeout(timer);
  }, [progress]);
  const uploadApi = useUploadApi();

  const [cancelTokenSource, setCancelTokenSource] = useState(() =>
    axios.CancelToken.source(),
  );

  const clear = () => {
    setUrl(null);
    setProgress(null);
  };

  const { open } = useFileSelector({
    accept: 'image/*',
    multiple: false,
    onChange: useCallback(
      async files => {
        if (!files || files.length < 1) return;

        const file = files.item(0) as File;
        if (file.size > maxMb * BYTES_PER_MB) {
          enqueueSnackbar(`Please upload a smaller image (< ${maxMb} MB).`, {
            variant: 'error',
            autoHideDuration: 6000,
          });
          return;
        }

        const newCancelTokenSource = axios.CancelToken.source();
        setCancelTokenSource(newCancelTokenSource);

        cancelTokenSource.cancel();
        const cancelToken = newCancelTokenSource.token;

        const result = await uploadApi.uploadImage(file, {
          cancelToken,
          onProgress: ({ loaded, total }) => {
            const newProgress = (loaded / total) * 100;
            setProgress(progress =>
              progress !== null || newProgress < 30 ? newProgress : null,
            );
          },
        });

        if (result.success) setUrl(result.value.url);
      },
      [cancelTokenSource, enqueueSnackbar, formatNumber, uploadApi],
    ),
  });

  return {
    open,
    clear,
    progress,
    url,
  };
};
